import type { RandomSource } from './types';

const UINT32_RANGE = 0x1_0000_0000;

function assertUnitFloat(value: number): number {
  if (!Number.isFinite(value) || value < 0 || value >= 1) {
    throw new RangeError('Die Zufallsquelle muss einen Wert im Bereich [0, 1) liefern.');
  }
  return value;
}

function assertIntegerBound(maxExclusive: number): void {
  if (!Number.isSafeInteger(maxExclusive) || maxExclusive <= 0) {
    throw new RangeError('Die exklusive Obergrenze muss eine positive sichere Ganzzahl sein.');
  }
}

abstract class FloatRandomSource implements RandomSource {
  abstract next(): number;

  integer(maxExclusive: number): number {
    assertIntegerBound(maxExclusive);
    return Math.floor(assertUnitFloat(this.next()) * maxExclusive);
  }
}

export type RandomValuesProvider = (buffer: Uint32Array) => Uint32Array;

function browserRandomValues(buffer: Uint32Array): Uint32Array {
  if (globalThis.crypto === undefined) {
    throw new Error('Kryptografischer Browserzufall ist nicht verfügbar.');
  }
  return globalThis.crypto.getRandomValues(buffer);
}

export class CryptoRandomSource implements RandomSource {
  constructor(private readonly getRandomValues: RandomValuesProvider = browserRandomValues) {}

  private uint32(): number {
    const values = this.getRandomValues(new Uint32Array(1));
    const value = values[0];
    if (value === undefined) {
      throw new Error('Die kryptografische Zufallsquelle hat keinen Wert geliefert.');
    }
    return value;
  }

  next(): number {
    return this.uint32() / UINT32_RANGE;
  }

  integer(maxExclusive: number): number {
    assertIntegerBound(maxExclusive);
    if (maxExclusive > UINT32_RANGE) {
      return Math.floor(this.next() * maxExclusive);
    }

    // Rejection sampling avoids modulo bias while retaining a single injected source.
    const limit = UINT32_RANGE - (UINT32_RANGE % maxExclusive);
    let value = this.uint32();
    while (value >= limit) value = this.uint32();
    return value % maxExclusive;
  }
}

/** Mulberry32, matching the deterministic Legacy analysis reports. */
export class SeededRandomSource extends FloatRandomSource {
  private state: number;

  constructor(seed: number) {
    super();
    if (!Number.isFinite(seed)) throw new RangeError('Seed muss endlich sein.');
    this.state = seed >>> 0;
  }

  next(): number {
    let value = (this.state += 0x6d2b79f5);
    value = Math.imul(value ^ (value >>> 15), value | 1);
    value ^= value + Math.imul(value ^ (value >>> 7), value | 61);
    return ((value ^ (value >>> 14)) >>> 0) / UINT32_RANGE;
  }
}

export class SequenceRandomSource extends FloatRandomSource {
  private cursor = 0;

  constructor(private readonly values: readonly number[]) {
    super();
    if (values.length === 0) throw new RangeError('Die Zufallssequenz darf nicht leer sein.');
    values.forEach(assertUnitFloat);
  }

  next(): number {
    const value = this.values[this.cursor % this.values.length];
    this.cursor += 1;
    if (value === undefined) throw new Error('Die Zufallssequenz ist unerwartet leer.');
    return value;
  }

  get consumed(): number {
    return this.cursor;
  }
}

